import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '../../api/client';
import './CategorySelector.css';
import { Icon } from '../Icon';
import { stripEmojis } from '../../utils/format';
import { Category } from '../../types';

interface CategorySelectorProps {
  value?: string;
  title: string;
  amount: number;
  isPremium: boolean;
  categories: Category[];
  onChange: (category: string) => void;
  onBack: () => void;
}

// Ключевые слова для автоопределения категории (только для системных категорий)
const categoryKeywords: Record<string, string[]> = {
  'Продукты': ['продукты', 'еда', 'магазин', 'супермаркет', 'groceries', 'food'],
  'Транспорт': ['транспорт', 'такси', 'бензин', 'автобус', 'метро', 'transport', 'taxi', 'gas'],
  'Кафе и рестораны': ['кафе', 'ресторан', 'кофе', 'пицца', 'бургер', 'cafe', 'restaurant'],
  'Развлечения': ['кино', 'игры', 'развлечения', 'парк', 'cinema', 'entertainment'],
  'Здоровье': ['аптека', 'врач', 'лекарство', 'больница', 'pharmacy', 'doctor', 'health'],
  'Одежда и обувь': ['одежда', 'обувь', 'магазин', 'clothes', 'shoes'],
  'Дом': ['ремонт', 'мебель', 'инструменты', 'furniture', 'home'],
  'Образование': ['курсы', 'учеба', 'книги', 'обучение', 'education', 'course'],
  'Связь': ['телефон', 'интернет', 'связь', 'phone', 'internet'],
  'Подписки': ['подписка', 'netflix', 'spotify', 'subscription'],
};

export const CategorySelector: React.FC<CategorySelectorProps> = ({
  value,
  title,
  amount,
  isPremium,
  categories,
  onChange,
  onBack
}) => {
  const { t } = useTranslation();
  console.log('🏷️ [CategorySelector] Rendering with:', {
    categoriesCount: categories.length,
    categories: categories.slice(0, 5),
    isPremium,
    value,
    title,
    amount
  });

  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(value);
  const [suggestedCategory, setSuggestedCategory] = useState<string | undefined>();
  const [customCategory, setCustomCategory] = useState('');
  const [showCustomInput, setShowCustomInput] = useState(false);
  const [isLoadingAI, setIsLoadingAI] = useState(false);

  // Автораспознавание категории на основе названия
  useEffect(() => {
    const suggestCategory = async () => {
      if (!title || value || categories.length === 0) return;

      // Сначала пробуем локальное определение
      const titleLower = title.toLowerCase();
      const localSuggested = categories.find(cat => {
        const keywords = categoryKeywords[cat.name] || [];
        return keywords.some(keyword => titleLower.includes(keyword));
      });
      
      if (localSuggested) {
        console.log('📁 Category suggested locally:', localSuggested.name);
        setSuggestedCategory(localSuggested.name);
        setSelectedCategory(localSuggested.name);
        return;
      }

      // Если локально не определили - пробуем через AI
      console.log('🤖 Local detection failed, trying AI...');
      setIsLoadingAI(true);
      try {
        const result = await apiClient.suggestCategory(title);
        if (result.success && result.category) {
          console.log('✅ AI suggested category:', result.category);
          setSuggestedCategory(result.category);
          setSelectedCategory(result.category);
        } else {
          console.log('⚠️ AI could not suggest category');
        }
      } catch (error) {
        console.error('❌ Failed to get AI category suggestion:', error);
      } finally {
        setIsLoadingAI(false);
      }
    };

    suggestCategory();
  }, [title, value, categories]);

  const handleSelectCategory = (category: string) => {
    setSelectedCategory(category);
    setShowCustomInput(false);
  };

  const handleSubmit = () => {
    if (showCustomInput && customCategory.trim()) {
      if (!isPremium) {
        alert(t('transaction.premiumCustomCategories'));
        return;
      }
      onChange(customCategory.trim());
    } else if (selectedCategory) {
      onChange(selectedCategory);
    }
  };

  const handleCreateCustom = () => {
    if (!isPremium) {
      alert(t('transaction.premiumCustomCategories'));
      return;
    }
    setShowCustomInput(true);
  };

  return (
    <div className="category-selector step-content">
      <div className="category-selector__header">
        <button className="category-selector__back neo-raised" onClick={onBack}>
          <Icon name="chevron_left" size={20} />
        </button>
        <div className="category-selector__header-content">
          <h2 className="category-selector__title">{t('transaction.category')}</h2>
          <p className="category-selector__subtitle">
            {isLoadingAI ? (
              <span className="category-selector__ai-loading">
                <Icon name="cpu" size={14} /> AI определяет категорию...
              </span>
            ) : suggestedCategory ? (
              <span className="category-selector__suggestion">
                <Icon name="lightbulb" size={14} /> Предлагаем: <strong>{suggestedCategory}</strong>
              </span>
            ) : null}
          </p>
        </div>
      </div>

      <div className="category-selector__content">
        {!showCustomInput ? (
          <>
            <div className="category-grid">
              {categories.map((category) => (
                <button
                  key={category.id}
                  className={`category-btn ${selectedCategory === category.name ? 'category-btn--active' : ''} ${category.name === suggestedCategory ? 'category-btn--suggested' : ''}`}
                  onClick={() => handleSelectCategory(category.name)}
                >
                  <div className="category-btn__emoji">
                    {category.emoji ? category.emoji : <Icon name="folder" size={24} />}
                  </div>
                  <div className="category-btn__name">{stripEmojis(category.name)}</div>
                  {category.name === suggestedCategory && (
                    <div className="category-btn__badge"><Icon name="star" size={14} /></div>
                  )}
                </button>
              ))}
            </div>

            <button
              className="category-selector__custom-btn"
              onClick={handleCreateCustom}
            >
              {isPremium ? 'Создать свою категорию' : (<><Icon name="lock" size={14} /> Создать свою (Premium)</>)}
            </button>
          </>
        ) : (
          <div className="category-selector__custom-input">
            <input
              type="text"
              className="custom-category-input"
              placeholder="Название категории..."
              value={customCategory}
              onChange={(e) => setCustomCategory(e.target.value)}
              maxLength={50}
            />
            <div className="custom-category-actions">
              <button
                className="custom-category-cancel"
                onClick={() => {
                  setShowCustomInput(false);
                  setCustomCategory('');
                }}
              >
                Отмена
              </button>
              <button
                className="custom-category-save"
                onClick={handleSubmit}
                disabled={!customCategory.trim()}
              >
                Создать
              </button>
            </div>
          </div>
        )}
      </div>

      {!showCustomInput && (
        <button
          className="category-selector__submit neo-raised"
          onClick={handleSubmit}
          disabled={!selectedCategory}
        >
          Далее
        </button>
      )}
    </div>
  );
};

